import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { 
  ArrowLeft, 
  Users, 
  Star, 
  Clock, 
  Play, 
  FileText, 
  Link as LinkIcon,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Award
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api';

interface Module {
  _id: string;
  title: string;
  description: string;
  type: string;
  duration: number;
  fileUrl?: string;
  externalUrl?: string;
  content?: string;
  isRequired: boolean;
  order: number;
}

interface Section {
  _id: string;
  title: string;
  description: string;
  order: number;
  modules: Module[];
  quizzes?: {
    _id: string;
    title: string;
    description: string;
    settings: {
      timeLimit: number;
      maxAttempts: number;
      passingScore: number;
    };
  }[];
}

interface Course {
  _id: string;
  title: string;
  description: string;
  thumbnail?: string;
  category: string;
  level: string;
  professor: {
    _id: string;
    name: string;
    avatar?: string;
  };
  duration: number;
  sections: Section[];
  quizzes: {
    _id: string;
    title: string;
    description: string;
    settings: {
      timeLimit: number;
      maxAttempts: number;
      passingScore: number;
    };
  }[];
  createdAt: string;
  status: string;
  students: Array<{
    _id: string;
    name: string;
    email: string;
  }>;
  userProgress: number;
  completedModules: string[];
}

const StudentCourseView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [userProgress, setUserProgress] = useState(0);
  const [completedModules, setCompletedModules] = useState<string[]>([]);

  useEffect(() => {
    loadCourse();
  }, [id]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/student/courses/${id}`);

      if (response.data) {
        setCourse(response.data.course);
        setUserProgress(response.data.userProgress || 0);
        setCompletedModules(response.data.completedModules || []);
        
        // Expand the first section by default
        if (response.data.course?.sections && response.data.course.sections.length > 0) {
          setExpandedSections([response.data.course.sections[0]._id]);
        }
      } else {
        toast.error('Erreur lors du chargement du cours');
        navigate('/student/courses');
      }
    } catch (error) {
      console.error('Load course error:', error);
      toast.error('Erreur lors du chargement du cours');
      navigate('/student/courses');
    } finally {
      setLoading(false);
    }
  };

  const toggleSectionExpand = (sectionId: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const getModuleIcon = (type: string) => {
    switch (type) {
      case 'video': return <Play className="h-4 w-4 text-brand-600" />;
      case 'pdf': return <FileText className="h-4 w-4 text-brand-600" />;
      case 'text': return <FileText className="h-4 w-4 text-gray-600" />;
      case 'link': return <LinkIcon className="h-4 w-4 text-brand-600" />;
      default: return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };

  const markModuleAsCompleted = async (moduleId: string) => {
    try {
      await api.post(`/student/modules/${moduleId}/complete`);
      // Update local state
      if (!completedModules.includes(moduleId)) {
        setCompletedModules([...completedModules, moduleId]);
        // Recalculate progress
        if (course) {
          const totalModules = course.sections.reduce(
            (total, section) => total + section.modules.length, 0);
          const newProgress = Math.round(((completedModules.length + 1) / totalModules) * 100);
          setUserProgress(newProgress);
        }
      }
      toast.success('Module marqué comme terminé');
    } catch (error) {
      console.error('Error marking module as completed:', error);
      toast.error('Erreur lors de la mise à jour du module');
    }
  };

  const startQuiz = async (quizId: string) => {
    try {
      const response = await api.post(`/student/quizzes/${quizId}/start`);
      toast.success(response.data.message);
      navigate(`/student/quiz/${quizId}/attempt/${response.data.attemptId}`);
    } catch (error) {
      console.error('Error starting quiz:', error);
      toast.error('Erreur lors du démarrage du quiz.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <FileText className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Cours non trouvé</h3>
          <p className="text-gray-500 mb-4">
            Le cours que vous recherchez n'existe pas ou a été supprimé.
          </p>
          <button
            onClick={() => navigate('/student/courses')}
            className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            Retour à la liste des cours
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/student/courses')}
              className="mr-4 text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">{course.title}</h1>
          </div>
        </div>

        {/* Course Overview */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3">
            <div className="md:col-span-1">
              <img
                src={course.thumbnail || 'https://images.pexels.com/photos/274422/pexels-photo-274422.jpeg'}
                alt={course.title}
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="md:col-span-2 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <span className="text-sm text-gray-500">
                  Créé le {new Date(course.createdAt).toLocaleDateString('fr-FR')}
                </span>
              </div>

              <p className="text-gray-700 mb-6">{course.description}</p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center">
                    <Users className="h-5 w-5 text-brand-600 mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Étudiants</div>
                      <div className="font-semibold">{course.students?.length || 0}</div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-brand-600 mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Durée</div>
                      <div className="font-semibold">{course.duration || 0}h</div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-brand-600 mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Modules</div>
                      <div className="font-semibold">
                        {course.sections?.reduce((total, section) => total + section.modules.length, 0) || 0}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-brand-500 mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Progression</div>
                      <div className="font-semibold">{userProgress}%</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div>
                  <span className="text-gray-600 font-medium">Catégorie:</span>
                  <span className="ml-2 capitalize">{course.category}</span>
                </div>
                <div>
                  <span className="text-gray-600 font-medium">Niveau:</span>
                  <span className="ml-2 capitalize">{course.level}</span>
                </div>
                <div>
                  <span className="text-gray-600 font-medium">Professeur:</span>
                  <span className="ml-2">{course.professor.name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Course Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Contenu du cours</h2>
          
          {course.sections && course.sections.length > 0 ? (
            <div className="space-y-4">
              {course.sections.map((section) => (
                <div key={section._id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div 
                    className="bg-gray-50 p-4 cursor-pointer flex justify-between items-center hover:bg-gray-100 transition-colors"
                    onClick={() => toggleSectionExpand(section._id)}
                  >
                    <h3 className="font-medium text-gray-900">
                      Section {section.order}: {section.title}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">
                        {section.modules.length} modules
                      </span>
                      {expandedSections.includes(section._id) ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  {expandedSections.includes(section._id) && (
                    <div className="p-4 border-t border-gray-200">
                      {section.modules.length > 0 ? (
                        <div className="space-y-2">
                          {section.modules.map((module) => (
                            <div key={module._id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                              <div className="mr-3">
                                {getModuleIcon(module.type)}
                              </div>
                              <div className="flex-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {module.title}
                                </span>
                                <span className="text-xs text-gray-500 ml-2">
                                  ({module.duration}min)
                                </span>
                                {completedModules.includes(module._id) && (
                                  <span className="ml-2 text-xs text-brand-600">
                                    <CheckCircle className="h-3 w-3 inline mr-1" />
                                    Terminé
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => {
                                  if (module.type === 'video' || module.type === 'pdf') {
                                    window.open(
                                      ((module.fileUrl || module.externalUrl) + ((module.fileUrl || module.externalUrl || '').includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(localStorage.getItem('token') || '')),
                                      '_blank'
                                    );
                                  } else if (module.type === 'link') {
                                    window.open(module.externalUrl, '_blank');
                                  }
                                }}
                                className="ml-auto px-3 py-1 bg-brand-600 text-white text-xs rounded-md hover:bg-brand-700 transition-colors"
                              >
                                {module.type === 'video' ? 'Regarder' : 
                                 module.type === 'pdf' ? 'Ouvrir' : 
                                 module.type === 'link' ? 'Visiter' : 'Voir'}
                              </button>
                              {!completedModules.includes(module._id) && (
                                <button
                                  onClick={() => markModuleAsCompleted(module._id)}
                                  className="ml-2 px-3 py-1 bg-brand-600 text-white text-xs rounded-md hover:bg-brand-700 transition-colors"
                                >
                                  Terminer
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          Aucun module dans cette section
                        </div>
                      )}
                      
                      {section.quizzes && section.quizzes.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {section.quizzes.map(quiz => (
                            <div key={quiz._id} className="p-3 bg-brand-50 rounded-lg border border-brand-200 flex items-center justify-between">
                              <div className="flex items-center">
                                <Award className="h-5 w-5 text-brand-600 mr-2" />
                                <div>
                                  <span className="font-medium text-gray-900">Quiz: {quiz.title}</span>
                                  <div className="text-xs text-brand-600">
                                    {quiz.settings.timeLimit > 0 && `${quiz.settings.timeLimit} min | `}
                                    {quiz.settings.maxAttempts > 0 ? `${quiz.settings.maxAttempts} tentatives` : 'Tentatives illimitées'}
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => startQuiz(quiz._id)}
                                className="px-3 py-1 bg-brand-600 text-white text-xs rounded-md hover:bg-brand-700 transition-colors"
                              >
                                Commencer le quiz
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <FileText className="h-16 w-16 mx-auto" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                Aucun contenu ajouté
              </h4>
              <p className="text-gray-500 mb-4">
                Ce cours ne contient pas encore de sections ou modules
              </p>
            </div>
          )}

          {course.quizzes && course.quizzes.length > 0 && (
            <div className="mt-6 p-6 bg-brand-50 rounded-lg border border-brand-200">
              <h3 className="text-xl font-bold text-brand-900 mb-4">Quiz du cours</h3>
              <div className="space-y-4">
                {course.quizzes.map(quiz => (
                  <div key={quiz._id} className="p-3 bg-white rounded-lg shadow-sm border border-brand-100 flex items-center justify-between">
                    <div className="flex items-center">
                      <Award className="h-5 w-5 text-brand-600 mr-2" />
                      <div>
                        <span className="font-medium text-gray-900">{quiz.title}</span>
                        <div className="text-xs text-brand-600">
                          {quiz.settings.timeLimit > 0 && `${quiz.settings.timeLimit} min | `}
                          {quiz.settings.maxAttempts > 0 ? `${quiz.settings.maxAttempts} tentatives` : 'Tentatives illimitées'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => startQuiz(quiz._id)}
                      className="px-3 py-1 bg-brand-600 text-white text-xs rounded-md hover:bg-brand-700 transition-colors"
                    >
                      Commencer le quiz
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default StudentCourseView;